/* eslint-disable prettier/prettier */
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { Field } from 'formik';
import Search from '@material-ui/icons/Search';
import { InputAdornment, MenuItem, Popper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import isUndefined from 'lodash/isUndefined';
import { useRouter } from 'next/router';
import TextField from './TextField';
import { colors, boxShadows, fontSize } from '../../../constants/styleConstants';
import AppCircularProgress from '../../AppCircularProgress/AppCircularProgress';

const useStyles = makeStyles({
  menu: {
    boxShadow: boxShadows.block,
    backgroundColor: colors.white,
    maxHeight: 200,
    overflow: 'auto',
    zIndex: 999,
  },
  linkOption: {
    color: colors.blue,
    cursor: 'pointer',
    textDecoration: 'underline',
    fontSize: fontSize.basic,
    '&:hover': {
      color: colors.darkBlue,
    },
  },
  active: {
    background: colors.background.optionBg,
    '& span': {
      color: colors.darkBlue,
    },
  },
  loadingIcon: {
    width: '20px !important',
    height: '20px !important',
    marginRight: 5,
    color: colors.blue3,
  },
});

const SearchField = ({
  name,
  label,
  options,
  onInputTextChange,
  onChange,
  onFocus,
  loading,
  className,
  placeholder,
  linkTypeOptions,
  setField,
  onEnter,
  isMainArtistSearchFilter,
}) => {
  const classes = useStyles();
  const SearchRef = useRef();

  const [anchorEl, setAnchorEl] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isMenuOpened, setMenuOpened] = useState(false);
  const router = useRouter();

  useEffect(() => {
    setAnchorEl(get(SearchRef, 'current'));
  }, []);

  useEffect(() => {
    const isArtistStubPage = !isUndefined(get(router, 'query.id'));
    if(!isArtistStubPage) {
      setIsOpen(false)
      if (!loading && get(options, 'length', []) > 0 && isOpen) {
        setMenuOpened(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, options]);

  const onInputChange = event => {
    const { value } = event.target;
    setField(value);
    onInputTextChange(value);
    if (!value) {
      setIsOpen(false);
      setMenuOpened(false);
    } else {
      setIsOpen(true);
      setMenuOpened(true);
    }
  };

  const handleClose = () => setMenuOpened(false);

  const onInputFocus = event => {
    setIsOpen(true);
    const { value } = event.target;
    setField(value);
    if (onFocus) {
      onFocus(value);
    }
  };
  const onInputBlur = () => {
    setField('');
    setIsOpen(false);
  };

  return (
    <>
      <Field
        className={className}
        key={name}
        id={name}
        name={name}
        label={label}
        placeholder={placeholder}
        component={TextField}
        onChange={onInputChange}
        onFocus={onInputFocus}
        onBlur={onInputBlur}
        onKeyDown={e => {
          if (e.keyCode === 13) {
            // Enter
            e.preventDefault();
            document.getElementById(name).blur();
            setMenuOpened(false);
            if (onEnter) {
              onEnter();
            }
          }
        }}
        inputProps={{
          // change loading indicator
          endAdornment: (
            <InputAdornment position="end">
              {loading && <AppCircularProgress className={classes.loadingIcon} />}
              <Search />
            </InputAdornment>
          ),
        }}
        isMainArtistSearchFilter
        innerRef={SearchRef}
      />
      {isMenuOpened && (
        <ClickAwayListener onClickAway={handleClose}>
          <Popper
            className={classes.menu}
            anchorEl={anchorEl}
            open={isMenuOpened}
            style={{ width: get(SearchRef, 'current.clientWidth') }}
          >
            {options.map((option) => {
              const optionValue = get(option, 'value', '');
              return (
                <MenuItem
                  key={`${name}-${optionValue}`}
                  onClick={() => {
                    onChange(option);
                    if (isMainArtistSearchFilter) {
                      setField('');
                      setIsOpen(false);
                    }
                    setMenuOpened(false);
                  }}
                  style={{ whiteSpace: 'normal' }}
                >
                  {linkTypeOptions ? (
                    <span className={classes.linkOption}>{get(option, 'label', '')}</span>
                  ) : (
                    get(option, 'label', '')
                  )}
                </MenuItem>
              );
            })}
          </Popper>
        </ClickAwayListener>
      )}
    </>
  );
};

SearchField.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.array,
  onInputTextChange: PropTypes.func,
  onChange: PropTypes.func,
  loading: PropTypes.bool,
  linkTypeOptions: PropTypes.bool,
  setField: PropTypes.func,
  onEnter: PropTypes.func,
  isMainArtistSearchFilter: PropTypes.bool,
};

SearchField.defaultProps = {
  placeholder: '',
  onChange: () => {},
  setField: () => {},
  linkTypeOptions: true,
  loading: false,
  options: [],
};

export default SearchField;
